/**
 * useEditProfile Hook
 * Custom hook for edit profile form using react-hook-form and Zod validation
 */

import { zodResolver } from "@hookform/resolvers/zod"
import type { ApiError } from "@shared-types/api.types"
import type { AxiosError } from "axios"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { transformApiError } from "@/lib/errorTransformer"
import type { EditProfileFormData } from "../schemas/edit-profile.schema"
import { editProfileSchema } from "../schemas/edit-profile.schema"

export function useEditProfile(
  onSubmit: (data: EditProfileFormData) => Promise<void>,
  defaultValues?: Partial<EditProfileFormData>,
) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)

  const form = useForm<EditProfileFormData>({
    resolver: zodResolver(editProfileSchema),
    mode: "onBlur",
    defaultValues: {
      name: defaultValues?.name || "",
      username: defaultValues?.username || "",
      city: defaultValues?.city || "",
      streetAddress: defaultValues?.streetAddress || "",
      state: defaultValues?.state || "",
      country: defaultValues?.country || "",
      zipCode: defaultValues?.zipCode || "",
      latitude: defaultValues?.latitude,
      longitude: defaultValues?.longitude,
      tags: defaultValues?.tags || [],
      website: defaultValues?.website || "",
      twitterUrl: defaultValues?.twitterUrl || "",
      instagramUrl: defaultValues?.instagramUrl || "",
      facebookUrl: defaultValues?.facebookUrl || "",
    },
  })

  const handleSubmit = async (e?: React.BaseSyntheticEvent) => {
    if (e) {
      e.preventDefault()
    }

    setIsSubmitting(true)
    setServerError(null)

    try {
      // Small delay to ensure form values are properly synchronized
      await new Promise(resolve => setTimeout(resolve, 10))

      // Validate with react-hook-form
      const isValid = await form.trigger()
      if (!isValid) {
        setIsSubmitting(false)
        return
      }

      // Get validated values
      const values = form.getValues()

      // Call submit handler
      await onSubmit(values)
    } catch (error) {
      // Transform error to user-friendly message
      const err = error as AxiosError<ApiError>
      const errorMessage = transformApiError(err)
      setServerError(errorMessage)
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    form,
    handleSubmit,
    isSubmitting,
    serverError,
    clearServerError: () => setServerError(null),
  }
}
